import React, { useContext, useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import * as Yup from "yup";
import { Form, Formik } from "formik";
import { Link, Box, Flex, Spinner } from "theme-ui";
import { themevals } from "theme/themevals";

import Header from "components/ui/Header";
import Page from "components/ui/Page";
import { BackArrowIcon, CheckMarkIcon } from "components/svg";
import TextInput from "components/forms/TextInput";
import { getRole, updateRole, deleteRole } from "api/Roles";
import BasicButton from "components/forms/BasicButton";
import { useToast } from "components/toast";
import Checkbox from "components/forms/Checkbox";
import TrashIcon from "components/svg/icons/TrashIcon";
import BasicModal from "components/ui/BasicModal";
import { QuestionMarkIcon } from "components/svg";
import WarningIcon from "components/svg/icons/WarningIcon";
import P from "components/typography/P";
import { trackActivity } from "api/ActivityTracker";
import UserContext from "context/User";
import {
  RolesTableWrapper,
  RolesTableLabel,
  RoleTable,
  RoleLinkWrapper,
  RoleSelectWrapper,
  RoleLink,
  ModelTitle,
  RoleSelectInner,
  RoleGroup,
  RoleGroupOptions,
  RoleGroupTitle,
  RolesTableFooter,
  BasicRoleInputs,
  RoleCheckWrapper,
  PermissionDescription,
} from "./styled";

export default function RoleEdit() {
  const toast = useToast();  

  const params = useParams();
  const [loaded, setLoaded] = useState<boolean>(false)
  const [role, setRole] = useState<any>(false);
  const [modelTitle, setModelTitle] = useState<string>("Company");
  const [model, setModel] = useState<string>("company");
  const [permissions, setPermissions] = useState<any>([]);
  const navigate = useNavigate();
  const userPermissions = ["userCreate", "userEdit", "userDelete", "userView"];
  const rfqPermissions = ["rfqCreate", "rfqEdit", "rfqDelete", "rfqView"];
  const rolePermissions = ["roleCreate", "roleEdit", "roleDelete", "roleView"];
  const aiAssistantPermissions = ["AI Assistant - Access", "AI Assistant - Write"];
  const dashboardWidgets: { key: string; label: string; description: string }[] = [
    { key: "dashboardKpiCards",            label: "KPI Cards",             description: "Top-of-page metric cards (quotes, revenue, active projects, margin)." },
    { key: "dashboardRevenueTrend",        label: "Revenue Trend",         description: "12-month revenue chart from payment activity." },
    { key: "dashboardPipelineFunnel",      label: "Pipeline Funnel",       description: "RFQ \u2192 Quote \u2192 Project \u2192 Invoice funnel with timeframe filters." },
    { key: "dashboardExpirationStats",     label: "Expiration Stats",      description: "Expiring quotes and RFQs summary." },
    { key: "dashboardPipelineMetrics",     label: "Pipeline Metrics",      description: "Pipeline health and conversion metrics." },
    { key: "dashboardOutstandingDeposits", label: "Outstanding Deposits",  description: "Price-lock deposits awaiting credit application." },
    { key: "dashboardActivePriceLocks",    label: "Active Price Locks",    description: "Currently locked quotes with expirations." },
    { key: "dashboardRecentActivity",      label: "Recent Activity",       description: "Feed of user actions across the CRM." },
    { key: "dashboardActionItems",         label: "Action Items",          description: "Items requiring attention (overdue invoices, stale quotes, etc.)." },
    { key: "dashboardCalendar",            label: "Calendar View",         description: "Monthly calendar of scheduled projects." },
  ];
  /**
   * Proposal permissions. Reading the list stays on company access — gating
   * reads would produce a role that can send a proposal but not see what it
   * sent. Creating and sending are separable because they are genuinely
   * different trusts: building a priced proposal is not the same act as
   * putting it in front of a prospect.
   */
  const proposalPermissions: { key: string; label: string; description: string }[] = [
    { key: "proposalView",   label: "View",   description: "See the proposals list, tracking and the document that was sent." },
    { key: "proposalCreate", label: "Create", description: "Build, price and edit proposals, and regenerate a new version of one." },
    { key: "proposalSend",   label: "Send",   description: "Email a proposal to a prospect and send it on to additional recipients." },
  ];

  /**
   * Lead permissions (Leads module). Same shape as proposals; leadConvert
   * is the one that creates a customer, so it is separate from Edit.
   */
  const leadPermissions: { key: string; label: string; description: string }[] = [
    { key: "leadView",    label: "View",    description: "See the leads list, a lead's record and its activity." },
    { key: "leadCreate",  label: "Create",  description: "Add leads by form, CSV import or from the proposal generator." },
    { key: "leadEdit",    label: "Edit",    description: "Change a lead's details, status, owner and next action; send quote links; mark lost; company Lead settings." },
    { key: "leadConvert", label: "Convert", description: "Turn a lead into a client, optionally inviting a portal user and creating the Zoho customer." },
  ];
  const [confirmModal, setConfirmModal] = useState<any>(false);
  const [confirmSubmit, setConfirmSubmit] = useState<boolean>(false);
  const { user } = useContext<any>(UserContext);
  const hasTrackedView = useRef(false);

  const initRole = async () => {   
    try {
      const res = await getRole({data : { id : params.id}});   
      setRole(!!res?.id ? res : null);  
      setPermissions(!!res?.permissions ? res.permissions : null);           
      setLoaded(true);               
    } catch (e) { }
  };

  const _onSubmit = async (values: any) => {
    const permissions: string[] = [];

    if (values.settingsFullAccess) {
      permissions.push("companyAccess");
    } else {
      Object.keys(values).forEach((key) => {
        if (
          key.startsWith("settings") && 
          key !== "settingsFullAccess" && 
          !key.includes("FullAccess") && 
          !key.includes("NoAccess") && 
          values[key] === true
        ) {
          permissions.push(key);
        }
      });
    }

    Object.keys(values).forEach((key) => {
      if (
        !key.startsWith("settings") && 
        !key.includes("FullAccess") && 
        !key.includes("NoAccess") && 
        values[key] === true
      ) {
        // Map AI Assistant form field names to actual permission strings
        if (key === "aiAssistantAccess") {
          permissions.push("AI Assistant - Access");
        } else if (key === "aiAssistantWrite") {
          permissions.push("AI Assistant - Write");
        } else {
          permissions.push(key);
        }
      }
    });

    const keys = ["title", "description"];
    const update = {
      ...Object.fromEntries(
        Object.entries(values).filter(([key]) => keys.includes(key))
      ),
      permissions, 
    };

    const previousRole = role;
    const res = await updateRole({ data: { id: params.id }, update });
    if(res?.id){
      toast.add("Role updated.","var(--theme-ui-colors-success)");

      const changedFields: string[] = [];
      if ((previousRole?.title ?? "") !== (res?.title ?? "")) {
        changedFields.push("title");
      }
      if ((previousRole?.description ?? "") !== (res?.description ?? "")) {
        changedFields.push("description");
      }

      const prevPermissions = Array.isArray(previousRole?.permissions)
        ? [...previousRole.permissions].sort()
        : [];
      const nextPermissions = Array.isArray(res?.permissions)
        ? [...res.permissions].sort()
        : [];

      if (JSON.stringify(prevPermissions) !== JSON.stringify(nextPermissions)) {
        changedFields.push("permissions");
      }

      if (changedFields.length > 0) {
        void trackActivity({
          module: "roles",
          action: "edit_role",
          actionType: "update",
          userId: user?.id || null,
          companyId: user?.company?.id || null,
          entityType: "role",
          entityId: res?.id ?? params.id ?? null,
          metadata: {
            changedFields,
          },
          userAgent: typeof navigator !== "undefined" ? navigator.userAgent : null,
        });
      }
    } else {  
      toast.add(res?.message ? res.message : "Error updating role","var(--theme-ui-colors-danger)");
    }   
    return true; 
  }
  const ValidateSchema = Yup.object().shape({
    title: Yup.string().required("This field is required."),
  }); 
  
  useEffect(() => {
    initRole()
  },[]);

  useEffect(() => {
    if (!loaded || !role?.id || hasTrackedView.current) {
      return;
    }
    hasTrackedView.current = true;
    void trackActivity({
      module: "roles",
      action: "view_roles_detail",
      actionType: "view",
      userId: user?.id || null,
      companyId: user?.company?.id || null,
      entityType: "role",
      entityId: role?.id ?? null,
      metadata: null,
      userAgent: typeof navigator !== "undefined" ? navigator.userAgent : null,
    });
    void trackActivity({
      module: "roles",
      action: "view_role_edit",
      actionType: "view",
      userId: user?.id || null,
      companyId: user?.company?.id || null,
      entityType: "role",
      entityId: role?.id ?? null,
      metadata: null,
      userAgent: typeof navigator !== "undefined" ? navigator.userAgent : null,
    });
  }, [loaded, role?.id, user?.id, user?.company?.id]);

  const handleLinkClick = (model: string, modelTitle: string) => {
    setModel(model);
    setModelTitle(modelTitle);
  };

  const confirmDeleteRole = async () => {
    setConfirmSubmit(true);   
    const res = await deleteRole({ data: role });
    if(res?.success){ 
      toast.add("Role deleted.","var(--theme-ui-colors-success)");  
      void trackActivity({
        module: "roles",
        action: "delete_role",
        actionType: "delete",
        userId: user?.id || null,
        companyId: user?.company?.id || null,
        entityType: "role",
        entityId: role?.id ?? null,
        metadata: null,
        userAgent: typeof navigator !== "undefined" ? navigator.userAgent : null,
      });
      setConfirmSubmit(false);   
      setConfirmModal(false);
      navigate(`/roles`); 
    }else{
      if(res?.statusCode === 409){
        setConfirmModal(
          <BasicModal 
            title="Cannot delete role!" 
            cancel_click={cancelDeleteRole} 
            cancel_text="Close" 
            size={"sm"}
            icon={<WarningIcon fill={"#818181"} height="30px"/>}  
          >
            <P lineHeight={'22px'}>There are users associated with this role.  Please change any users with this role before attempting to delete.</P>
          </BasicModal>);
      }else{
        toast.add(res?.message ? res.message : "Error deleting role.","var(--theme-ui-colors-danger)");
      }
    } 
  }

  const cancelDeleteRole = async () => {
    setConfirmModal(false)
    setConfirmSubmit(false);
  }

  const submitDeleteRole = async () => {
    setConfirmModal(
    <BasicModal 
      title="Delete role?" 
      cancel_click={cancelDeleteRole} 
      cancel_text="No, cancel" 
      $submitting={confirmSubmit}
      size={"sm"}
      icon={<QuestionMarkIcon fill={"#818181"} height="30px"/>}
      actions={
  
  
          <BasicButton
            type="button"    
            scheme="danger"      
            onClick={() => confirmDeleteRole()} 
            $submitting={confirmSubmit}
            $iconHeight="14px"
            $iconWidth="14px"
            $loadingColor="#fff"
            keepText={true}
            styles={{margin:"0"}}
          >
            {!confirmSubmit && <TrashIcon fill={"#fff"}/>}        
            Delete
          </BasicButton>
       
      }    
    >
      <P>Are you sure you want to delete this role?</P>
    </BasicModal>);
  }

  return (
    <>
      <Header>
      </Header>      
      <Page 
        title="Edit Role" 
        subtitle="Edit a role with specific permissions for your users."
        backAction={<Link variant="back" href="/roles"><BackArrowIcon />Back to Roles</Link>}
      >      
        {!loaded && <Box sx={{ textAlign: "center" }}><Spinner size={25} strokeWidth={3} title="loading" /></Box>}
        {loaded && 
          <Formik initialValues={{
            title: role?.title ? role.title : "",
            description: role?.description ? role.description : "",
            settingsFullAccess: permissions.includes("companyAccess") ? true : false,
            settingsNoAccess: !permissions.includes("companyAccess") ? true : false,
            userFullAccess: userPermissions.every((perm) => permissions.includes(perm)) ? true : false,
            userNoAccess: !userPermissions.some((perm) => permissions.includes(perm)) ? true : false,
            userCreate: permissions.includes("userCreate") ? true : false,
            userView: permissions.includes("userView") ? true : false,
            userEdit: permissions.includes("userEdit") ? true : false,
            userDelete: permissions.includes("userDelete") ? true : false,
            rfqFullAccess: rfqPermissions.every((perm) => permissions.includes(perm)) ? true : false,
            rfqNoAccess: !rfqPermissions.some((perm) => permissions.includes(perm)) ? true : false,
            rfqCreate: permissions.includes("rfqCreate") ? true : false,
            rfqEdit: permissions.includes("rfqEdit") ? true : false,
            rfqDelete: permissions.includes("rfqDelete") ? true : false,
            rfqView: permissions.includes("rfqView") ? true : false,
            rfqMyView: permissions.includes("rfqMyView") ? true : false,
            rfqMyEdit: permissions.includes("rfqMyEdit") ? true : false,
            rfqMyDelete: permissions.includes("rfqMyDelete") ? true : false,
            roleFullAccess: rolePermissions.every((perm) => permissions.includes(perm)) ? true : false,
            roleNoAccess: !rolePermissions.some((perm) => permissions.includes(perm)) ? true : false,
            roleView: permissions.includes("roleView") ? true : false,
            roleCreate: permissions.includes("roleCreate") ? true : false,
            roleEdit: permissions.includes("roleEdit") ? true : false,
            roleDelete: permissions.includes("roleDelete") ? true : false,
            aiAssistantFullAccess: aiAssistantPermissions.every((perm) => permissions.includes(perm)) ? true : false,
            aiAssistantNoAccess: !aiAssistantPermissions.some((perm) => permissions.includes(perm)) ? true : false,
            aiAssistantAccess: permissions.includes("AI Assistant - Access") ? true : false,
            aiAssistantWrite: permissions.includes("AI Assistant - Write") ? true : false,
            ...Object.fromEntries(dashboardWidgets.map(w => [w.key, permissions.includes(w.key)])),
            ...Object.fromEntries(proposalPermissions.map(p => [p.key, permissions.includes(p.key)])),
            ...Object.fromEntries(leadPermissions.map(p => [p.key, permissions.includes(p.key)])),
            // Only tick Full Access when every widget permission is present.
            // Never auto-tick No Access on load: an unticked role means "back-compat, show all",
            // which is not the same as "user explicitly hid everything". Ticking No Access here
            // would falsely imply the latter, and would contradict the footnote copy.
            dashboardFullAccess: dashboardWidgets.every(w => permissions.includes(w.key)),
            dashboardNoAccess: false,
            proposalFullAccess: proposalPermissions.every(p => permissions.includes(p.key)),
            proposalNoAccess: false,
            leadFullAccess: leadPermissions.every(p => permissions.includes(p.key)),
            leadNoAccess: false,
          }} onSubmit={_onSubmit} validationSchema={ValidateSchema} enableReinitialize>
            {({ isSubmitting, errors, values, submitCount, setFieldValue }: any) => { 
              const handleSimpleFullAccess = (val: any, model: string) => {
                if (val) {
                  setFieldValue(`${model}NoAccess`, false);
                  setFieldValue(`${model}Create`, true);
                  setFieldValue(`${model}View`, true);
                  setFieldValue(`${model}Edit`, true);
                  setFieldValue(`${model}Delete`, true);
                  setFieldValue(`${model}FullAccess`, true);
                }
              };
  
              const handleSimpleNoAccess = (val: any, model: string) => {
                if (val) {
                  setFieldValue(`${model}FullAccess`, false);
                  setFieldValue(`${model}Create`, false);
                  setFieldValue(`${model}View`, false);
                  setFieldValue(`${model}Edit`, false);
                  setFieldValue(`${model}Delete`, false);
                  setFieldValue(`${model}NoAccess`, true);
                }
              };
  
              const handleSimpleToggle = (
                val: boolean,
                name: string,
                model: string
              ) => {
                setFieldValue(name, val);
  
                const isAllUnchecked =
                  (name === `${model}Create`
                    ? !val
                    : !values[`${model}Create`]) &&
                  (name === `${model}View` ? !val : !values[`${model}View`]) &&
                  (name === `${model}Edit` ? !val : !values[`${model}Edit`]) &&
                  (name === `${model}Delete` ? !val : !values[`${model}Delete`]);
  
                const isAllChecked =
                  (name === `${model}Create` ? val : values[`${model}Create`]) &&
                  (name === `${model}View` ? val : values[`${model}View`]) &&
                  (name === `${model}Edit` ? val : values[`${model}Edit`]) &&
                  (name === `${model}Delete` ? val : values[`${model}Delete`]);
  
                if (!val) {
                  setFieldValue(`${model}FullAccess`, false);
                  setFieldValue(`${model}NoAccess`, isAllUnchecked);
                } else {
                  setFieldValue(`${model}NoAccess`, false);
                  setFieldValue(`${model}FullAccess`, isAllChecked);
                }
              };
  
              const handleComplexFullAccess = (
                val: any,
                name: string,
                model: string
              ) => {
                if (val) {
                  setFieldValue(`${model}FullAccess`, true);
                  setFieldValue(`${model}NoAccess`, false);
                  setFieldValue(`${model}Create`, true);
                  setFieldValue(`${model}Edit`, true);
                  setFieldValue(`${model}View`, true);
                  setFieldValue(`${model}Delete`, true);
                  setFieldValue(`${model}MyEdit`, false);
                  setFieldValue(`${model}MyView`, false);
                  setFieldValue(`${model}MyDelete`, false);
                }
              };
  
              const handleComplexNoAccess = (
                val: any,
                name: string,
                model: string
              ) => {
                if (val) {
                  setFieldValue(`${model}NoAccess`, true);
                  setFieldValue(`${model}FullAccess`, false);
                  setFieldValue(`${model}Create`, false);
                  setFieldValue(`${model}Edit`, false);
                  setFieldValue(`${model}View`, false);
                  setFieldValue(`${model}Delete`, false);
                  setFieldValue(`${model}MyDelete`, false);
                  setFieldValue(`${model}MyView`, false);
                  setFieldValue(`${model}MyEdit`, false);
                }
              };
  
              const handleComplexToggle = (
                val: boolean,
                name: string,
                model: string,
                action: string
              ) => {
                setFieldValue(name, val);
                const isAllUnchecked =
                  (name === `${model}Create`
                    ? !val
                    : !values[`${model}Create`]) &&
                  (name === `${model}View` ? !val : !values[`${model}View`]) &&
                  (name === `${model}Edit` ? !val : !values[`${model}Edit`]) &&
                  (name === `${model}Delete`
                    ? !val
                    : !values[`${model}Delete`]) &&
                  (name === `${model}MyDelete`
                    ? !val
                    : !values[`${model}MyDelete`]) &&
                  (name === `${model}MyEdit`
                    ? !val
                    : !values[`${model}MyEdit`]) &&
                  (name === `${model}MyView` ? !val : !values[`${model}MyView`]);
  
                const isAllChecked =
                  (name === `${model}Create` ? val : values[`${model}Create`]) &&
                  (name === `${model}View` ? val : values[`${model}View`]) &&
                  (name === `${model}Edit` ? val : values[`${model}Edit`]) &&
                  (name === `${model}Delete` ? val : values[`${model}Delete`]);
  
                if (!val) {
                  setFieldValue(`${model}FullAccess`, false);
                  setFieldValue(`${model}NoAccess`, isAllUnchecked);
                } else {
                  setFieldValue(`${model}NoAccess`, false);
                  setFieldValue(`${model}FullAccess`, isAllChecked);
  
                  if (name === `${model}${action}`) {
                    setFieldValue(`${model}My${action}`, false);
                    setFieldValue(`${model}${action}`, true);
                  } else {
                    setFieldValue(`${model}${action}`, false);
                    setFieldValue(`${model}My${action}`, true);
                  }
                }
  
                if (name.startsWith(`${model}My`)) {
                  setFieldValue(`${model}FullAccess`, false); // Always uncheck FullAccess if a My field is toggled
                  setFieldValue(`${model}${action}`, false);
                }
              };
              return (                
              <Form noValidate>
                <BasicRoleInputs>
                <TextInput
                  name="title"
                  label="Role Name"
                  value={values.title}
                  onChange={(val: any) => setFieldValue("title",val)}
                  $errors={errors.title && submitCount > 0 ? errors.title : null}  
                  $customStyles={{ width: "100%", maxWidth: "400px" }}                                 
                /> 
                <TextInput
                  name="description"
                  type="textarea"
                  label="Role Description"
                  value={values.description}
                  onChange={(val: any) => setFieldValue("description",val)}
                  $errors={errors.description && submitCount > 0 ? errors.description : null}  
                  $customStyles={{ width: "100%", maxWidth: "800px"}}
                  rows={1}                                
                /> 
                </BasicRoleInputs>
                <RolesTableWrapper>
                  <RolesTableLabel>Permissions</RolesTableLabel>
                  <RoleTable>
                    <RoleLinkWrapper>
                      <RoleLink
                        className={
                          modelTitle === "Company" ? "active" : ""
                        }
                        onClick={() =>
                          handleLinkClick(
                            "company",
                            "Company"
                          )
                        }
                      >
                        Company
                      </RoleLink>
                      <RoleLink
                        className={modelTitle === "Users" ? "active" : ""}
                        onClick={() => handleLinkClick("users", "Users")}
                      >
                        Users
                      </RoleLink>
                      <RoleLink
                        className={modelTitle === "Roles" ? "active" : ""}
                        onClick={() => handleLinkClick("role", "Roles")}
                      >
                        Roles
                      </RoleLink>
                      <RoleLink
                        className={modelTitle === `${"RFQ's"}` ? "active" : ""}
                        onClick={() => handleLinkClick("rfq", `${"RFQ's"}`)}
                      >{`${"RFQ's"}`}</RoleLink>
                      <RoleLink
                        className={modelTitle === "AI Assistant" ? "active" : ""}
                        onClick={() => handleLinkClick("aiAssistant", "AI Assistant")}
                      >AI Assistant</RoleLink>
                      <RoleLink
                        className={modelTitle === "Dashboard" ? "active" : ""}
                        onClick={() => handleLinkClick("dashboard", "Dashboard")}
                      >Dashboard</RoleLink>
                      <RoleLink
                        className={modelTitle === "Proposals" ? "active" : ""}
                        onClick={() => handleLinkClick("proposal", "Proposals")}
                      >Proposals</RoleLink>
                      <RoleLink
                        className={modelTitle === "Leads" ? "active" : ""}
                        onClick={() => handleLinkClick("lead", "Leads")}
                      >Leads</RoleLink>
                    </RoleLinkWrapper>
                    <RoleSelectWrapper>
                      <ModelTitle>{modelTitle}</ModelTitle>
                      <RoleSelectInner>
                        {model === "company" && (
                          <>
                            <RoleGroup>
                              <RoleGroupTitle>Full Access</RoleGroupTitle>
                              <RoleGroupOptions>
                                <Checkbox
                                  checked={values.settingsFullAccess}
                                  name="settingsFullAccess"
                                  onChange={(val: any) => {
                                    handleSimpleFullAccess(val, "settings");
                                  }}
                                />
                              </RoleGroupOptions>
                            </RoleGroup>
                            <RoleGroup>
                              <RoleGroupTitle>No Access</RoleGroupTitle>
                              <RoleGroupOptions>
                                <Checkbox
                                  checked={values.settingsNoAccess}
                                  name="settingsNoAccess"
                                  onChange={(val: any) => {
                                    handleSimpleNoAccess(val, "settings");
                                  }}
                                />
                              </RoleGroupOptions>
                            </RoleGroup>
                          </>
                        )}
                        {model === "users" && (
                          <>
                            <RoleGroup>
                              <RoleGroupTitle>Full Access</RoleGroupTitle>
                              <RoleGroupOptions>
                                <Checkbox
                                  checked={values.userFullAccess}
                                  name="userFullAccess"
                                  onChange={(val: any) => {
                                    handleSimpleFullAccess(val, "user");
                                  }}
                                />
                              </RoleGroupOptions>
                            </RoleGroup>
                            <RoleGroup>
                              <RoleGroupTitle>No Access</RoleGroupTitle>
                              <RoleGroupOptions>
                                <Checkbox
                                  checked={values.userNoAccess}
                                  name="userNoAccess"
                                  onChange={(val: any) => {
                                    handleSimpleNoAccess(val, "user");
                                  }}
                                />
                              </RoleGroupOptions>
                            </RoleGroup>
                            <RoleGroup>
                              <RoleGroupTitle>Create</RoleGroupTitle>
                              <RoleGroupOptions>
                                <Checkbox
                                  checked={values.userCreate}
                                  name="userCreate"
                                  onChange={(val: any) => {
                                    handleSimpleToggle(
                                      val,
                                      "userCreate",
                                      "user"
                                    );
                                  }}
                                />
                              </RoleGroupOptions>
                            </RoleGroup>
                            <RoleGroup>
                              <RoleGroupTitle>View</RoleGroupTitle>
                              <RoleGroupOptions>
                                <Checkbox
                                  checked={values.userView}
                                  name="userView"
                                  onChange={(val: any) => {
                                    handleSimpleToggle(val, "userView", "user");
                                  }}
                                />
                              </RoleGroupOptions>
                            </RoleGroup>
                            <RoleGroup>
                              <RoleGroupTitle>Edit</RoleGroupTitle>
                              <RoleGroupOptions>
                                <Checkbox
                                  checked={values.userEdit}
                                  name="userEdit"
                                  onChange={(val: any) => {
                                    handleSimpleToggle(val, "userEdit", "user");
                                  }}
                                />
                              </RoleGroupOptions>
                            </RoleGroup>
                            <RoleGroup>
                              <RoleGroupTitle>Delete</RoleGroupTitle>
                              <RoleGroupOptions>
                                <Checkbox
                                  checked={values.userDelete}
                                  name="userDelete"
                                  onChange={(val: any) => {
                                    handleSimpleToggle(
                                      val,
                                      "userDelete",
                                      "user"
                                    );
                                  }}
                                />
                              </RoleGroupOptions>
                            </RoleGroup>
                          </>
                        )}
                        {model === "rfq" && (
                          <>
                            <RoleGroup>
                              <RoleGroupTitle>Full Access</RoleGroupTitle>
                              <RoleGroupOptions>
                                <Checkbox
                                  checked={values.rfqFullAccess}
                                  name="rfqFullAccess"
                                  onChange={(val: any) => {
                                    handleComplexFullAccess(
                                      val,
                                      "rfqFullAccess",
                                      "rfq"
                                    );
                                  }}
                                />
                              </RoleGroupOptions>
                            </RoleGroup>
                            <RoleGroup>
                              <RoleGroupTitle>No Access</RoleGroupTitle>
                              <RoleGroupOptions>
                                <Checkbox
                                  checked={values.rfqNoAccess}
                                  name="rfqNoAccess"
                                  onChange={(val: any) => {
                                    handleComplexNoAccess(
                                      val,
                                      "rfqNoAccess",
                                      "rfq"
                                    );
                                  }}
                                />
                              </RoleGroupOptions>
                            </RoleGroup>
                            <RoleGroup>
                              <RoleGroupTitle>Create</RoleGroupTitle>
                              <RoleGroupOptions>
                                <Checkbox
                                  checked={values.rfqCreate}
                                  name="rfqCreate"
                                  onChange={(val: any) => {
                                    handleComplexToggle(
                                      val,
                                      "rfqCreate",
                                      "rfq",
                                      "Create"
                                    );
                                  }}
                                />
                              </RoleGroupOptions>
                            </RoleGroup>
                            <RoleGroup>
                              <RoleGroupTitle multi={true}>View</RoleGroupTitle>
                              <RoleGroupOptions>
                                <RoleCheckWrapper top={true}>
                                  <Checkbox
                                    width={"250px"}
                                    labelSize={"14px"}
                                    label="All RFQ's"
                                    checked={values.rfqView}
                                    name="rfqView"
                                    onChange={(val: any) => {
                                      handleComplexToggle(
                                        val,
                                        "rfqView",
                                        "rfq",
                                        "View"
                                      );
                                    }}
                                  />
                                  <PermissionDescription
                                    style={{ flex: "1 1 auto" }}
                                  >{`User will be able to view all RFQ's created by your company.`}</PermissionDescription>
                                </RoleCheckWrapper>
                                <RoleCheckWrapper>
                                  <Checkbox
                                    width={"250px"}
                                    labelSize={"14px"}
                                    label="RFQ's created by user"
                                    checked={values.rfqMyView}
                                    name="rfqMyView"
                                    onChange={(val: any) => {
                                      handleComplexToggle(
                                        val,
                                        "rfqMyView",
                                        "rfq",
                                        "View"
                                      );
                                    }}
                                  />
                                  <PermissionDescription
                                    style={{ flex: "1 1 auto" }}
                                  >{`User will be able to view all RFQ's created by them.`}</PermissionDescription>
                                </RoleCheckWrapper>
                              </RoleGroupOptions>
                            </RoleGroup>
                            <RoleGroup>
                              <RoleGroupTitle multi={true}>Edit</RoleGroupTitle>
                              <RoleGroupOptions>
                                <RoleCheckWrapper top={true}>
                                  <Checkbox
                                    width={"250px"}
                                    labelSize={"14px"}
                                    label="All RFQ's"
                                    checked={values.rfqEdit}
                                    name="rfqEdit"
                                    onChange={(val: any) => {
                                      handleComplexToggle(
                                        val,
                                        "rfqEdit",
                                        "rfq",
                                        "Edit"
                                      );
                                    }}
                                  />
                                  <PermissionDescription
                                    style={{ flex: "1 1 auto" }}
                                  >{`User will be able to edit all RFQ's created by your company.`}</PermissionDescription>
                                </RoleCheckWrapper>
                                <RoleCheckWrapper>
                                  <Checkbox
                                    width={"250px"}
                                    labelSize={"14px"}
                                    label="RFQ's created by user"
                                    checked={values.rfqMyEdit}
                                    name="rfqMyEdit"
                                    onChange={(val: any) => {
                                      handleComplexToggle(
                                        val,
                                        "rfqMyEdit",
                                        "rfq",
                                        "Edit"
                                      );
                                    }}
                                  />
                                  <PermissionDescription
                                    style={{ flex: "1 1 auto" }}
                                  >{`User will be able to edit all RFQ's created by them.`}</PermissionDescription>
                                </RoleCheckWrapper>
                              </RoleGroupOptions>
                            </RoleGroup>
                            <RoleGroup>
                              <RoleGroupTitle multi={true}>
                                Delete
                              </RoleGroupTitle>
                              <RoleGroupOptions>
                                <RoleCheckWrapper top={true}>
                                  <Checkbox
                                    width={"250px"}
                                    labelSize={"14px"}
                                    label="All RFQ's"
                                    checked={values.rfqDelete}
                                    name="rfqDelete"
                                    onChange={(val: any) => {
                                      handleComplexToggle(
                                        val,
                                        "rfqDelete",
                                        "rfq",
                                        "Delete"
                                      );
                                    }}
                                  />
                                  <PermissionDescription
                                    style={{ flex: "1 1 auto" }}
                                  >{`User will be able to delete all RFQ's created by your company.`}</PermissionDescription>
                                </RoleCheckWrapper>
                                <RoleCheckWrapper>
                                  <Checkbox
                                    width={"250px"}
                                    labelSize={"14px"}
                                    label="RFQ's created by user"
                                    checked={values.rfqMyDelete}
                                    name="rfqMyDelete"
                                    onChange={(val: any) => {
                                      handleComplexToggle(
                                        val,
                                        "rfqMyDelete",
                                        "rfq",
                                        "Delete"
                                      );
                                    }}
                                  />
                                  <PermissionDescription
                                    style={{ flex: "1 1 auto" }}
                                  >{`User will be able to delete all RFQ's created by them.`}</PermissionDescription>
                                </RoleCheckWrapper>
                              </RoleGroupOptions>
                            </RoleGroup>
                          </>
                        )}
                        {model === "role" && (
                          <>
                            <RoleGroup>
                              <RoleGroupTitle>Full Access</RoleGroupTitle>
                              <RoleGroupOptions>
                                <Checkbox
                                  checked={values.roleFullAccess}
                                  name="roleFullAccess"
                                  onChange={(val: any) => {
                                    handleSimpleFullAccess(val, "role");
                                  }}
                                />
                              </RoleGroupOptions>
                            </RoleGroup>
                            <RoleGroup>
                              <RoleGroupTitle>No Access</RoleGroupTitle>
                              <RoleGroupOptions>
                                <Checkbox
                                  checked={values.roleNoAccess}
                                  name="roleNoAccess"
                                  onChange={(val: any) => {
                                    handleSimpleNoAccess(val, "role");
                                  }}
                                />
                              </RoleGroupOptions>
                            </RoleGroup>
                            <RoleGroup>
                              <RoleGroupTitle>Create</RoleGroupTitle>
                              <RoleGroupOptions>
                                <Checkbox
                                  checked={values.roleCreate}
                                  name="roleCreate"
                                  onChange={(val: any) => {
                                    handleSimpleToggle(
                                      val,
                                      "roleCreate",
                                      "role"
                                    );
                                  }}
                                />
                              </RoleGroupOptions>
                            </RoleGroup>
                            <RoleGroup>
                              <RoleGroupTitle>View</RoleGroupTitle>
                              <RoleGroupOptions>
                                <Checkbox
                                  checked={values.roleView}
                                  name="roleView"
                                  onChange={(val: any) => {
                                    handleSimpleToggle(val, "roleView", "role");
                                  }}
                                />
                              </RoleGroupOptions>
                            </RoleGroup>
                            <RoleGroup>
                              <RoleGroupTitle>Edit</RoleGroupTitle>
                              <RoleGroupOptions>
                                <Checkbox
                                  checked={values.roleEdit}
                                  name="roleEdit"
                                  onChange={(val: any) => {
                                    handleSimpleToggle(val, "roleEdit", "role");
                                  }}
                                />
                              </RoleGroupOptions>
                            </RoleGroup>
                            <RoleGroup>
                              <RoleGroupTitle>Delete</RoleGroupTitle>
                              <RoleGroupOptions>
                                <Checkbox
                                  checked={values.roleDelete}
                                  name="roleDelete"
                                  onChange={(val: any) => {
                                    handleSimpleToggle(
                                      val,
                                      "roleDelete",
                                      "role"
                                    );
                                  }}
                                />
                              </RoleGroupOptions>
                            </RoleGroup>
                          </>
                        )}
                        {model === "aiAssistant" && (
                          <>
                            <RoleGroup>
                              <RoleGroupTitle>Full Access</RoleGroupTitle>
                              <RoleGroupOptions>
                                <Checkbox
                                  checked={values.aiAssistantFullAccess}
                                  name="aiAssistantFullAccess"
                                  onChange={(val: any) => {
                                    if (val) {
                                      setFieldValue("aiAssistantFullAccess", true);
                                      setFieldValue("aiAssistantNoAccess", false);
                                      setFieldValue("aiAssistantAccess", true);
                                      setFieldValue("aiAssistantWrite", true);
                                    }
                                  }}
                                />
                              </RoleGroupOptions>
                            </RoleGroup>
                            <RoleGroup>
                              <RoleGroupTitle>No Access</RoleGroupTitle>
                              <RoleGroupOptions>
                                <Checkbox
                                  checked={values.aiAssistantNoAccess}
                                  name="aiAssistantNoAccess"
                                  onChange={(val: any) => {
                                    if (val) {
                                      setFieldValue("aiAssistantNoAccess", true);
                                      setFieldValue("aiAssistantFullAccess", false);
                                      setFieldValue("aiAssistantAccess", false);
                                      setFieldValue("aiAssistantWrite", false);
                                    }
                                  }}
                                />
                              </RoleGroupOptions>
                            </RoleGroup>
                            <RoleGroup>
                              <RoleGroupTitle multi={true}>Access (Read-only)</RoleGroupTitle>
                              <RoleGroupOptions>
                                <RoleCheckWrapper top={true}>
                                  <Checkbox
                                    width={"250px"}
                                    labelSize={"14px"}
                                    label="View financial data"
                                    checked={values.aiAssistantAccess}
                                    name="aiAssistantAccess"
                                    onChange={(val: any) => {
                                      setFieldValue("aiAssistantAccess", val);
                                      if (!val) {
                                        setFieldValue("aiAssistantFullAccess", false);
                                        setFieldValue("aiAssistantWrite", false);
                                        setFieldValue("aiAssistantNoAccess", !val && !values.aiAssistantWrite);
                                      } else {
                                        setFieldValue("aiAssistantNoAccess", false);
                                        setFieldValue("aiAssistantFullAccess", val && values.aiAssistantWrite);
                                      }
                                    }}
                                  />
                                  <PermissionDescription style={{ flex: "1 1 auto" }}>
                                    User can query financial data, view invoices, bills, and run database queries.
                                  </PermissionDescription>
                                </RoleCheckWrapper>
                              </RoleGroupOptions>
                            </RoleGroup>
                            <RoleGroup>
                              <RoleGroupTitle multi={true}>Write</RoleGroupTitle>
                              <RoleGroupOptions>
                                <RoleCheckWrapper top={true}>
                                  <Checkbox
                                    width={"250px"}
                                    labelSize={"14px"}
                                    label="Save reports"
                                    checked={values.aiAssistantWrite}
                                    name="aiAssistantWrite"
                                    onChange={(val: any) => {
                                      setFieldValue("aiAssistantWrite", val);
                                      if (!val) {
                                        setFieldValue("aiAssistantFullAccess", false);
                                      } else {
                                        setFieldValue("aiAssistantNoAccess", false);
                                        setFieldValue("aiAssistantFullAccess", val && values.aiAssistantAccess);
                                      }
                                    }}
                                  />
                                  <PermissionDescription style={{ flex: "1 1 auto" }}>
                                    User can save audit reports and analysis to S3 storage.
                                  </PermissionDescription>
                                </RoleCheckWrapper>
                              </RoleGroupOptions>
                            </RoleGroup>
                          </>
                        )}
                        {model === "dashboard" && (
                          <>
                            <RoleGroup>
                              <RoleGroupTitle>Full Access</RoleGroupTitle>
                              <RoleGroupOptions>
                                <Checkbox
                                  checked={values.dashboardFullAccess}
                                  name="dashboardFullAccess"
                                  onChange={(val: any) => {
                                    if (val) {
                                      setFieldValue("dashboardFullAccess", true);
                                      setFieldValue("dashboardNoAccess", false);
                                      dashboardWidgets.forEach(w => setFieldValue(w.key, true));
                                    }
                                  }}
                                />
                              </RoleGroupOptions>
                            </RoleGroup>
                            <RoleGroup>
                              <RoleGroupTitle>No Access</RoleGroupTitle>
                              <RoleGroupOptions>
                                <Checkbox
                                  checked={values.dashboardNoAccess}
                                  name="dashboardNoAccess"
                                  onChange={(val: any) => {
                                    if (val) {
                                      setFieldValue("dashboardNoAccess", true);
                                      setFieldValue("dashboardFullAccess", false);
                                      dashboardWidgets.forEach(w => setFieldValue(w.key, false));
                                    }
                                  }}
                                />
                              </RoleGroupOptions>
                            </RoleGroup>
                            <RoleGroup>
                              <RoleGroupTitle multi={true}>Widgets</RoleGroupTitle>
                              <RoleGroupOptions>
                                {dashboardWidgets.map((w, idx) => (
                                  <RoleCheckWrapper key={w.key} top={idx === 0}>
                                    <Checkbox
                                      width={"250px"}
                                      labelSize={"14px"}
                                      label={w.label}
                                      checked={!!values[w.key]}
                                      name={w.key}
                                      onChange={(val: any) => {
                                        setFieldValue(w.key, val);
                                        const nextAllChecked = dashboardWidgets.every(x => x.key === w.key ? val : !!values[x.key]);
                                        setFieldValue("dashboardFullAccess", nextAllChecked);
                                        // Deliberately do NOT auto-tick No Access when all widgets are
                                        // unchecked. Empty widget list = back-compat "show all", not
                                        // "user wanted nothing to show". Only an explicit No Access click
                                        // means "hide everything".
                                        if (val) setFieldValue("dashboardNoAccess", false);
                                      }}
                                    />
                                    <PermissionDescription style={{ flex: "1 1 auto" }}>
                                      {w.description}
                                    </PermissionDescription>
                                  </RoleCheckWrapper>
                                ))}
                              </RoleGroupOptions>
                            </RoleGroup>
                            <div style={{ padding: "12px 20px", fontSize: 12, color: "#888", fontStyle: "italic" }}>
                              Widget visibility applies to Admin and Manager users. If none are checked and “No Access” isn’t explicitly selected on a fresh role, all widgets show by default. Set “No Access” to explicitly hide every widget.
                            </div>
                          </>
                        )}
                        {model === "proposal" && (
                          <>
                            <RoleGroup>
                              <RoleGroupTitle>Full Access</RoleGroupTitle>
                              <RoleGroupOptions>
                                <Checkbox
                                  checked={values.proposalFullAccess}
                                  name="proposalFullAccess"
                                  onChange={(val: any) => {
                                    if (val) {
                                      setFieldValue("proposalFullAccess", true);
                                      setFieldValue("proposalNoAccess", false);
                                      proposalPermissions.forEach(p => setFieldValue(p.key, true));
                                    }
                                  }}
                                />
                              </RoleGroupOptions>
                            </RoleGroup>
                            <RoleGroup>
                              <RoleGroupTitle>No Access</RoleGroupTitle>
                              <RoleGroupOptions>
                                <Checkbox
                                  checked={values.proposalNoAccess}
                                  name="proposalNoAccess"
                                  onChange={(val: any) => {
                                    if (val) {
                                      setFieldValue("proposalNoAccess", true);
                                      setFieldValue("proposalFullAccess", false);
                                      proposalPermissions.forEach(p => setFieldValue(p.key, false));
                                    }
                                  }}
                                />
                              </RoleGroupOptions>
                            </RoleGroup>
                            <RoleGroup>
                              <RoleGroupTitle multi={true}>Permissions</RoleGroupTitle>
                              <RoleGroupOptions>
                                {proposalPermissions.map((p, idx) => (
                                  <RoleCheckWrapper key={p.key} top={idx === 0}>
                                    <Checkbox
                                      width={"250px"}
                                      labelSize={"14px"}
                                      label={p.label}
                                      checked={!!values[p.key]}
                                      name={p.key}
                                      onChange={(val: any) => {
                                        setFieldValue(p.key, val);
                                        const nextAllChecked = proposalPermissions.every(x => x.key === p.key ? val : !!values[x.key]);
                                        setFieldValue("proposalFullAccess", nextAllChecked);
                                        if (val) setFieldValue("proposalNoAccess", false);
                                      }}
                                    />
                                    <PermissionDescription style={{ flex: "1 1 auto" }}>
                                      {p.description}
                                    </PermissionDescription>
                                  </RoleCheckWrapper>
                                ))}
                              </RoleGroupOptions>
                            </RoleGroup>
                            <div style={{ padding: "12px 20px", fontSize: 12, color: "#888", fontStyle: "italic" }}>
                              A super admin bypasses these entirely. Proposals carry provider cost and margin, so these permissions are for internal staff only — they are never granted to a client or provider role.
                            </div>
                          </>
                        )}
                        {model === "lead" && (
                          <>
                            <RoleGroup>
                              <RoleGroupTitle>Full Access</RoleGroupTitle>
                              <RoleGroupOptions>
                                <Checkbox
                                  checked={values.leadFullAccess}
                                  name="leadFullAccess"
                                  onChange={(val: any) => {
                                    if (val) {
                                      setFieldValue("leadFullAccess", true);
                                      setFieldValue("leadNoAccess", false);
                                      leadPermissions.forEach(p => setFieldValue(p.key, true));
                                    }
                                  }}
                                />
                              </RoleGroupOptions>
                            </RoleGroup>
                            <RoleGroup>
                              <RoleGroupTitle>No Access</RoleGroupTitle>
                              <RoleGroupOptions>
                                <Checkbox
                                  checked={values.leadNoAccess}
                                  name="leadNoAccess"
                                  onChange={(val: any) => {
                                    if (val) {
                                      setFieldValue("leadNoAccess", true);
                                      setFieldValue("leadFullAccess", false);
                                      leadPermissions.forEach(p => setFieldValue(p.key, false));
                                    }
                                  }}
                                />
                              </RoleGroupOptions>
                            </RoleGroup>
                            <RoleGroup>
                              <RoleGroupTitle multi={true}>Permissions</RoleGroupTitle>
                              <RoleGroupOptions>
                                {leadPermissions.map((p, idx) => (
                                  <RoleCheckWrapper key={p.key} top={idx === 0}>
                                    <Checkbox
                                      width={"250px"}
                                      labelSize={"14px"}
                                      label={p.label}
                                      checked={!!values[p.key]}
                                      name={p.key}
                                      onChange={(val: any) => {
                                        setFieldValue(p.key, val);
                                        const nextAllChecked = leadPermissions.every(x => x.key === p.key ? val : !!values[x.key]);
                                        setFieldValue("leadFullAccess", nextAllChecked);
                                        if (val) setFieldValue("leadNoAccess", false);
                                      }}
                                    />
                                    <PermissionDescription style={{ flex: "1 1 auto" }}>
                                      {p.description}
                                    </PermissionDescription>
                                  </RoleCheckWrapper>
                                ))}
                              </RoleGroupOptions>
                            </RoleGroup>
                            <div style={{ padding: "12px 20px", fontSize: 12, color: "#888", fontStyle: "italic" }}>
                              Leads are internal only. The Leads menu appears when a role has View and the company has the Leads module switched on (Lead settings).
                            </div>
                          </>
                        )}
                      </RoleSelectInner>
                    </RoleSelectWrapper>
                  </RoleTable>
                  <RolesTableFooter>
                    <Flex sx={{display: "flex", flexWrap : "wrap-reverse", gap : "20px", justifyContent : "space-between", width : "100%"}}>
                      <Box>
                        <BasicButton 
                          scheme="dangerSecondary"
                          styles={{margin:".25rem 0.625rem .25rem 0"}}
                          type="button" 
                          onClick={() => submitDeleteRole()} 
                          $iconHeight="14px"
                          $iconWidth="14px"
                          $loadingColor="#fff"
                        ><TrashIcon fill={themevals.colors.danger}/>Delete</BasicButton> 
                      </Box>
                      <Flex sx={{display: "flex", flexWrap : "wrap-reverse"}}>
                        <BasicButton
                          outline={true}
                          styles={{ margin: ".25rem 20px .25rem 0" }}
                          type="button"
                          onClick={() => navigate("/roles")}
                          scheme="secondary"
                        >Cancel</BasicButton>                    
                        <BasicButton
                          scheme="primary"
                          type="submit"
                          styles={{ margin: ".25rem 0px .25rem 0" }}
                          $submitting={isSubmitting}
                          $iconHeight="14px"
                          $loadingColor="#fff"
                          keepText={true}
                        >{!isSubmitting && <CheckMarkIcon fill={"#fff"} />} Save Role</BasicButton>
                      </Flex>
                    </Flex>
                  </RolesTableFooter>
                </RolesTableWrapper>
              </Form>
              );
            }}
          </Formik>
        }
      </Page> 
      {confirmModal}     
    </>
  );
}
